import { ILeisureWebApiProduct } from '~/api/services/leisure-web-api/index.type'
import { DateTime } from 'luxon'
import { isTruthy } from '~/api/utils/isTruthy'

export const ConfigBadgeType = {
  PENDING_RESERVATION: 'PENDING_RESERVATION',
  CANCEL_PENALTY: 'CANCEL_PENALTY',
  MILLION_COUPON: 'MILLION_COUPON',
  SP_MILLION_COUPON: 'SP_MILLION_COUPON',
  Y_FLUX: 'Y_FLUX',
  MILLION_COUPON_MOTEL: 'MILLION_COUPON_MOTEL',
  LEISURE_COUPON: 'LEISURE_COUPON',
  DAY_USE: 'DAY_USE',
  /** 킹특가, 최저가보상 */
  ONE_OFF_PROMOTION: 'ONE_OFF_PROMOTION',
  PAYBACK: 'PAYBACK',
  /** 레저 > 레포츠대전 */
  LEISURE_PLAY_BATTLE: 'LEISURE_PLAY_BATTLE',
} as const

export type TConfigBadgeType = keyof typeof ConfigBadgeType

export type GetLeisureValidityPeriodParams = {
  currentAt: string
  validityStartDateTime?: string
  validityEndDateTime?: string
}

export const productEventBadge = {
  PLAY_BATTLE: ConfigBadgeType.LEISURE_PLAY_BATTLE,
}

export const getBadgeTypeWithProductEvent = ({
  productEvent,
  currentAt,
}: {
  productEvent: ILeisureWebApiProduct['productEvent']
  currentAt: string
}): string[] => {
  if (!productEvent) return []
  const { startDateTime, endDateTime } = productEvent

  if (!startDateTime || !endDateTime) return []

  const isUsableDate =
    DateTime.fromFormat(startDateTime, 'yyyy-MM-dd HH:mm:ss')
      .diff(DateTime.fromISO(currentAt))
      .toMillis() < 0 &&
    DateTime.fromFormat(endDateTime, 'yyyy-MM-dd HH:mm:ss')
      .diff(DateTime.fromISO(currentAt))
      .toMillis() > 0

  const badge = productEventBadge[productEvent.productEventTypeCode]
  return isUsableDate && badge ? [badge] : []
}

/**
 * @summary 레저 상품 유효 기간 조회 (from 배리언트) - 장바구니, 주문서 (preOrder)
 * @description 주문 완료 이전 단계에서는 현재 시간 비교 로직이 있음.
 * {@link https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/90623336/02.} - AC
 * {@link https://yanolja.atlassian.net/wiki/spaces/FES/pages/103745201/FES+Component} - spec 문서
 * @test test/app/utils/leisureProductHelper.spec.ts
 *
 * @param {boolean} validityStartDateTime 유효기간 시작일 - yyyy-MM-dd HH:mm
 * @param {string} validityEndDateTime 유효기간 종료일 - yyyy-MM-dd HH:mm
 * @param {string} currentAt 현재 시간 (serverCurrentAt)
 *
 * @returns {string}
 */
export const getPreOrderLeisureValidityPeriod = ({
  validityStartDateTime,
  validityEndDateTime,
  currentAt,
}: GetLeisureValidityPeriodParams): string => {
  const today = DateTime.fromISO(currentAt)
  /**
   * 유효기간 : {date(StartDateTime)} ~ {date(endDateTime)} 까지
   * StartDateTime 가 없는 경우
   * 유효기간 : {date(endDateTime)} 까지
   *
   * validityEndDateTime 은 하루를 빼줘야함.
   * ex. 2023-03-21 23:59:59 로 넘어오는 경우 2023-03-21 23:59:59.001 대응이 되지 않아
   * 2023-03-22 00:00:00 으로 응답됨.
   */
  const validityStartDateTimeObj = validityStartDateTime
    ? DateTime.fromFormat(validityStartDateTime, 'yyyy-MM-dd HH:mm')
    : ''

  /**
   * validityStartDateTime 이 현재시간 보다 이전인 경우
   * 유효기간 시작날짜는 현재시간을 기준으로 노출함.
   */
  const startDateTimeObj = validityStartDateTimeObj
    ? validityStartDateTimeObj > today
      ? validityStartDateTimeObj
      : today
    : undefined
  const endDateTimeObj = validityEndDateTime
    ? DateTime.fromFormat(validityEndDateTime, 'yyyy-MM-dd HH:mm').minus({ milliseconds: 1 })
    : undefined

  return getLeisureProductValidityPeriodText({
    startDate: startDateTimeObj,
    endDate: endDateTimeObj,
  })
}

const convertLeisureProductDateFormat = (date: DateTime): string => {
  return date.toFormat('yyyy.MM.dd')
}

/**
 * @summary 레저 상품 유효 기간 조회 (from 배리언트) -> text 변경 공통 로직
 * {@link https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/90623336/02.} - AC
 * {@link https://yanolja.atlassian.net/wiki/spaces/FES/pages/103745201/FES+Component} - spec 문서
 * @test test/app/utils/leisureProductHelper.spec.ts
 *
 * @param {DateTime | undefined} startDate 유효기간 시작일
 * @param {DateTime | undefined} endDate 유효기간 종료일 (validityEndDateTime)
 *
 * @returns {string}
 */
export const getLeisureProductValidityPeriodText = ({
  startDate,
  endDate,
}: {
  startDate?: DateTime
  endDate?: DateTime
}): string => {
  const start = startDate ? convertLeisureProductDateFormat(startDate) : ''
  const end = endDate ? convertLeisureProductDateFormat(endDate) : ''

  /**
   * validityEndDateTime 이 startDate 보다 이전인 경우 유효기간 미노출
   * startDate 는 현재 시간일 수 있음. (-> 장바구니, 주문서)
   */
  if (!!startDate && !!endDate && endDate < startDate) {
    return ''
  }

  if (!start && !end) {
    return ''
  }
  /**
   * validityStartDate 만 있는 경우
   */
  if (!!start && !end) {
    return `유효기간 : ${start} ~`
  }

  /**
   * startDate 와 endDate 가 같은 경우
   * 유효기간 : ${endDate} 까지 노출
   */
  if (start === end) {
    return `유효기간 : ${end} 까지`
  }

  const date = [start, end].filter(isTruthy)
  return `유효기간 : ${date.join(' ~ ')} 까지`
}
/**
 * @summary 레저 상품 유효 기간 조회 (from 배리언트) - 주문 완료 이후 단계 (postOrder)
 * @description 주문 완료 이후 단계에서는 현재 시간 비교 로직이 없음.
 * {@link https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/90623336/02.} - AC
 * {@link https://yanolja.atlassian.net/wiki/spaces/FES/pages/103745201/FES+Component} - spec 문서
 * @test test/app/utils/leisureProductHelper.spec.ts
 *
 * @param {boolean} validityStartDateTime 유효기간 시작일 - yyyy-MM-dd HH:mm
 * @param {string} validityEndDateTime 유효기간 종료일 - yyyy-MM-dd HH:mm
 *
 * @returns {string}
 */
export const getPostOrderLeisureValidityPeriod = ({
  validityStartDateTime,
  validityEndDateTime,
}: Omit<GetLeisureValidityPeriodParams, 'currentAt'>): string => {
  /**
   * 유효기간 : {date(StartDateTime)} ~ {date(endDateTime)} 까지
   * StartDateTime 가 없는 경우
   * 유효기간 : {date(endDateTime)} 까지
   *
   * validityEndDateTime 은 하루를 빼줘야함.
   * ex. 2023-03-21 23:59:59 로 넘어오는 경우 2023-03-21 23:59:59.001 대응이 되지 않아
   * 2023-03-22 00:00:00 으로 응답됨.
   */
  const startDateTimeObj = validityStartDateTime
    ? DateTime.fromISO(validityStartDateTime)
    : undefined

  const endDateTimeObj = validityEndDateTime
    ? DateTime.fromISO(validityEndDateTime).minus({ milliseconds: 1 })
    : undefined

  return getLeisureProductValidityPeriodText({
    startDate: startDateTimeObj,
    endDate: endDateTimeObj,
  })
}
